import { useState, useCallback, useMemo } from "react";
import { useCategoryAttributesDisplay } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import { useTags } from "@/features/admin/queries/useTags";
import type { Category } from "@/services/api/categories/types";
import type { Tag } from "@/services/api/tags/types";

interface UseUploadModalOptions {
  category: Category | null;
  enabled?: boolean;
}

export function useUploadModal({
  category,
  enabled = true,
}: UseUploadModalOptions) {
  const [attributeValues, setAttributeValues] = useState<
    Record<string | number, string | number>
  >({});
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagSearchQuery, setTagSearchQuery] = useState("");

  const { attributes: categoryAttributes, isLoading } =
    useCategoryAttributesDisplay({
      category,
      enabled: enabled && !!category,
    });

  const { data: availableTags = [], isLoading: tagsLoading } = useTags({
    enabled,
  });

  const requiredAttributes = useMemo(
    () => categoryAttributes.filter((attr) => attr.isRequired),
    [categoryAttributes]
  );
  const optionalAttributes = useMemo(
    () => categoryAttributes.filter((attr) => !attr.isRequired),
    [categoryAttributes]
  );

  const selectedTags = useMemo<Tag[]>(
    () => availableTags.filter((tag) => selectedTagIds.includes(tag.id)),
    [availableTags, selectedTagIds]
  );

  const filteredAvailableTags = useMemo<Tag[]>(() => {
    const query = tagSearchQuery.toLowerCase();
    return availableTags.filter(
      (tag) =>
        tag.isActive &&
        !selectedTagIds.includes(tag.id) &&
        (tag.name?.toLowerCase().includes(query) ?? false)
    );
  }, [availableTags, selectedTagIds, tagSearchQuery]);

  const handleAttributeChange = useCallback(
    (attributeId: string | number, value: string | number | undefined) => {
      setAttributeValues((prev) => {
        if (value === undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [attributeId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [attributeId]: value };
      });
    },
    []
  );

  const handleTagToggle = useCallback((tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  }, []);

  const resetState = useCallback(() => {
    setAttributeValues({});
    setSelectedTagIds([]);
    setTagSearchQuery("");
  }, []);

  const allRequiredFilled = requiredAttributes.every(
    (attr) => attr.id in attributeValues
  );

  return {
    attributeValues,
    requiredAttributes,
    optionalAttributes,
    categoryAttributes,
    isLoading,
    allRequiredFilled,
    handleAttributeChange,
    resetState,
    selectedTagIds,
    selectedTags,
    filteredAvailableTags,
    tagsLoading,
    tagSearchQuery,
    setTagSearchQuery,
    handleTagToggle,
  };
}
